import { useMemo } from 'react';
import { useTheme } from '../../theme';
import { ContainerHeaderProps, ContainerProps } from './model';

export interface UseContainerLayoutProps
  extends Pick<ContainerProps, 'disablePadding' | 'collapsed'>,
    Pick<ContainerHeaderProps, 'variant'> {}

export interface UseContainerLayoutResponse {
  container: {
    /**
     * Border width to use on the `Container` when it is nested.
     */
    nestedBorderWidth: number;
    paddingX: number;
    paddingY: number;
  };
  containerBodyInner: {
    borderBottomLeftRadius: number;
    borderBottomRightRadius: number;
    paddingX: number;
    paddingY: number;
  };
  /**
   * Default container settings that are not impacted by things like disablePadding. Useful for certain calculations.
   */
  containerDefault: {
    paddingX: number;
    paddingY: number;
  };
  header: {
    height: number;

    // Note: the margins DO NOT impact downstream use in `VizContainer`, which does variant-related setup for the
    // header with a different approach than `Container` because of diverging requirements/apis.
    marginBottom: number;
    marginTop: number;
    marginX: number;
  };
}

/**
 * Hook that provides some constants related to the layout of the `Container`.
 * Primarily used by `VizPanel` to programmatically handle panel layouts and ensure they stay in
 * sync with the `Container`.
 */
export const useContainerLayout = ({
  // These values are passed in as options instead of being directly grabbed from the container context because of
  // some complex interdependencies with `VizContainer`.
  disablePadding,
  variant,
  collapsed,
}: UseContainerLayoutProps = {}): UseContainerLayoutResponse => {
  const { theme } = useTheme();

  // Note: we parse the numbers from the pixel values we get back from `theme.spacing` because we
  // need these numbers for doing math.
  const spacingPixels = parseFloat(theme.spacing(1));
  const borderRadius: number =
    typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : parseFloat(theme.shape.borderRadius);

  const containerDefault: UseContainerLayoutResponse['containerDefault'] = useMemo(() => {
    return {
      paddingY: spacingPixels,
      paddingX: spacingPixels * 2,
    };
  }, [spacingPixels]);

  const container: UseContainerLayoutResponse['container'] = useMemo(() => {
    const nestedBorderWidth = 1;

    if (disablePadding) {
      return {
        nestedBorderWidth,
        paddingX: 0,
        paddingY: 0,
      };
    }

    return {
      nestedBorderWidth,
      paddingX: containerDefault.paddingX,
      paddingY: containerDefault.paddingY,
    };
  }, [containerDefault.paddingX, containerDefault.paddingY, disablePadding]);

  const header: UseContainerLayoutResponse['header'] = useMemo(() => {
    const height = 32;

    // The top-level container does not have variant information (it's only on the header component), so adjusting the
    // bottom margin to match designs when the Container is open.
    const isSubtleMarginBottom = variant === 'subtle' && !collapsed;

    if (disablePadding) {
      // When padding is disabled, the header cannot rely on the outer container padding to
      // properly space the header, so we instead need to set it on the header itself. Margins are
      // used instead of padding here because we are generating spacing between the header and
      // the container (not inside the header), and it avoids more complex css issues with padding,
      // height, and box-sizing for the header.
      return {
        height,
        marginX: containerDefault.paddingX,
        marginTop: containerDefault.paddingY,
        marginBottom: isSubtleMarginBottom ? 0 : containerDefault.paddingY,
      };
    }

    return {
      height,
      marginX: 0,
      marginTop: 0,
      marginBottom: isSubtleMarginBottom ? -containerDefault.paddingY : 0,
    };
  }, [collapsed, containerDefault.paddingX, containerDefault.paddingY, disablePadding, variant]);

  const containerBodyInner: UseContainerLayoutResponse['containerBodyInner'] = useMemo(() => {
    if (disablePadding) {
      return {
        paddingX: 0,
        paddingY: 0,

        // When padding is disabled the body inner's bottom border radii need to match the `Container` to
        // avoid sharp edges showing.
        borderBottomLeftRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
      };
    }

    return {
      paddingX: 0,
      paddingY: spacingPixels,

      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    };
  }, [borderRadius, disablePadding, spacingPixels]);

  return useMemo(() => {
    return {
      container,
      containerDefault,
      header,
      containerBodyInner,
    };
  }, [container, containerDefault, header, containerBodyInner]);
};
